import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO component to dynamically manage page metadata.
 * 
 * @param {Object} props
 * @param {string} props.title - The page title (recommended max 60 chars)
 * @param {string} props.description - The page meta description (recommended max 160 chars)
 * @param {string} [props.ogType="website"] - The Open Graph type
 * @param {string} [props.ogImage] - The Open Graph image URL
 */
export default function SEO({ title, description, ogType = "website", ogImage }) {
  const location = useLocation();
  const currentUrl = window.location.origin + location.pathname;
  
  // Default fallback image (the SECoaching logo or hero image)
  const defaultImage = "https://assets.auxodev.dk/assets/06e215ef-94f8-4c3c-bc53-7852c768946e/d138cc09-7f90-4b04-aaa9-ca1750da8b92.jpeg";
  const finalImage = ogImage || defaultImage;

  useEffect(() => {
    // 1. Update Document Title
    if (title) {
      document.title = title;
    }

    // Helper function to find or create meta tags
    const setMetaTag = (attributeName, attributeValue, contentValue) => {
      if (!contentValue) return;
      
      let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', contentValue);
    };

    // 2. Update Meta Description
    setMetaTag('name', 'description', description);

    // 3. Update Open Graph (Facebook, LinkedIn, etc.)
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:url', currentUrl);
    setMetaTag('property', 'og:image', finalImage);
    setMetaTag('property', 'og:site_name', 'SECoaching');

    // 4. Update Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', finalImage);

    // 5. Update Canonical Link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', currentUrl);

  }, [title, description, ogType, currentUrl, finalImage]);

  return null;
}
